import { useState } from 'react';
import Switch from 'react-switch';
import styled from 'styled-components';

import { colors } from 'constants/colors';
import { PAGES } from 'constants/pages';
import { GetActiveOrForDateOrdersQuery, OrderState, useGetActiveOrForDateOrdersQuery } from 'data/__generated__';

import { Header, HeaderTitle } from 'components/Header';
import { Loader } from 'components/Loader';
import { PageTitle } from 'components/PageTitle';
import { TotemLabel } from 'components/TotemLabel';
import { TotemInput } from 'components/TotemInput';
import { TrelloView } from 'components/TrelloView/TrelloView';
import { OrderTrelloItem } from 'pages/Orders/components/OrderTrelloItem';

import { TotemDatePicker } from 'components/TotemDatePicker';
import { dateFromString, formatDateAsAnniversary } from 'helpers/dateTimes';
import { useSearchValueParam } from 'helpers/SearchParams/utils';

export const ORDER_COLUMN_LABELS = {
    archived: 'Archivées',
    created: 'Créées',
    validated: 'Validées',
    readyRecurring: 'Prêtes récurrentes',
    readyPunctual: 'Prêtes ponctuelles',
    onboarding: 'Première commande',
    pausedToReactivate: 'En pause à reactiver',
    paused: 'En pause',
    draftNProspects: 'Prospect',
    inProgress: 'En cours',
};

export const Orders = () => {
    const { searchInputValue, searchValue, setSearchInputValue } = useSearchValueParam();
    const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
    const [deliverySearchDate, setDeliverySearchDate] = useState<Date | null>(null);
    const [sortByName, setSortByName] = useState(true);
    const {
        data: ordersData,
        loading: ordersLoading,
        error: ordersError,
    } = useGetActiveOrForDateOrdersQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            deliveryDate: deliverySearchDate ? formatDateAsAnniversary({ dateTime: deliverySearchDate }) : null,
        },
    });

    if (ordersLoading && !ordersData) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (ordersError || !ordersData) {
        throw new Error('Une erreur est survenue lors de la récupération des commandes');
    }

    const { activeOrForDateOrders: unsortedOrders } = ordersData;

    // Filtrer les commandes selon la recherche
    const filteredOrders = unsortedOrders.filter(
        (order) =>
            !searchValue ||
            order._id.toLowerCase() === searchValue.toLowerCase() ||
            order.organization.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
            order.number?.toString().toLowerCase().includes(searchValue.toLowerCase()) ||
            order.totem?.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
            order.totem?.number?.toString().toLowerCase().includes(searchValue.toLowerCase()),
    );

    const orders = sortByName
        ? [...filteredOrders].sort((orderA, orderB) => (orderA.organization.name > orderB.organization.name ? 1 : -1))
        : [...filteredOrders].sort((orderA, orderB) =>
              !orderB.date_delivery ||
              (orderA.date_delivery && dateFromString(orderA.date_delivery) > dateFromString(orderB.date_delivery))
                  ? -1
                  : 1,
          );

    const today = new Date();
    const todayNumeric = today.setHours(0, 0, 0, 0);

    const {
        archived,
        readyRecurring,
        readyPunctual,
        validated,
        onboarding,
        pausedToReactivate,
        paused,
        draftNProspects,
        inProgress,
        created,
    } = orders.reduce<{
        archived: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        readyRecurring: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        readyPunctual: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        validated: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        onboarding: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        pausedToReactivate: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        paused: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        draftNProspects: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        inProgress: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
        created: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'];
    }>(
        (acc, order) => {
            if (order.state === OrderState.Archived) {
                acc.archived.push(order);
            } else if (order.state === OrderState.Ready) {
                if (order.isPunctual) {
                    acc.readyPunctual.push(order);
                } else {
                    acc.readyRecurring.push(order);
                }
            } else if (order.state === OrderState.Validated) {
                if (order.first_order) {
                    acc.onboarding.push(order);
                } else {
                    acc.validated.push(order);
                }
            } else if (order.state === OrderState.Paused) {
                if (order?.pausedInfo?.returnDate) {
                    if (order?.pausedInfo?.returnDate > todayNumeric) {
                        acc.paused.push(order);
                    } else {
                        acc.pausedToReactivate.push(order);
                    }
                } else {
                    acc.paused.push(order);
                }
            } else if ([OrderState.Created, OrderState.InProgress].includes(order.state)) {
                if (order.isPunctual || order.first_order) {
                    acc.draftNProspects.push(order);
                } else {
                    if (order.state === OrderState.InProgress) {
                        acc.inProgress.push(order);
                    } else {
                        acc.created.push(order);
                    }
                }
            }

            return acc;
        },
        {
            archived: [],
            readyRecurring: [],
            readyPunctual: [],
            validated: [],
            onboarding: [],
            pausedToReactivate: [],
            paused: [],
            draftNProspects: [],
            inProgress: [],
            created: [],
        },
    );

    function toggleOrderSelection(orderIdToToggle: string) {
        if (selectedOrderIds.includes(orderIdToToggle)) {
            setSelectedOrderIds(selectedOrderIds.filter((OrderId) => OrderId !== orderIdToToggle));
        } else {
            setSelectedOrderIds([...selectedOrderIds, orderIdToToggle]);
        }
    }

    function mapOrdersToTrelloItems(
        ordersToMap: GetActiveOrForDateOrdersQuery['activeOrForDateOrders'],
        shouldShowState = false,
    ) {
        return ordersToMap.map((order) => ({
            _id: order._id,
            content: (
                <OrderTrelloItem
                    order={order}
                    isSelected={selectedOrderIds.includes(order._id)}
                    toggleSelection={() => toggleOrderSelection(order._id)}
                    shouldShowState={shouldShowState}
                />
            ),
        }));
    }

    const initialData = {
        columns: [
            {
                title: ORDER_COLUMN_LABELS.readyRecurring,
                items: mapOrdersToTrelloItems(readyRecurring),
            },

            {
                title: ORDER_COLUMN_LABELS.readyPunctual,
                items: mapOrdersToTrelloItems(readyPunctual),
            },
            {
                title: ORDER_COLUMN_LABELS.validated,
                items: mapOrdersToTrelloItems(validated),
            },
            {
                title: ORDER_COLUMN_LABELS.onboarding,
                items: mapOrdersToTrelloItems(onboarding),
            },
            {
                title: ORDER_COLUMN_LABELS.inProgress,
                items: mapOrdersToTrelloItems(inProgress),
            },
            {
                title: ORDER_COLUMN_LABELS.pausedToReactivate,
                items: mapOrdersToTrelloItems(pausedToReactivate),
            },
            {
                title: ORDER_COLUMN_LABELS.created,
                items: mapOrdersToTrelloItems(created),
            },
            {
                title: ORDER_COLUMN_LABELS.paused,
                items: mapOrdersToTrelloItems(paused),
            },
            {
                title: ORDER_COLUMN_LABELS.draftNProspects,
                items: mapOrdersToTrelloItems(draftNProspects),
            },
            {
                title: ORDER_COLUMN_LABELS.archived,
                items: mapOrdersToTrelloItems(archived),
            },
        ],
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.orders} />
                </HeaderTitle>
                <Filters>
                    <SearchContainer>
                        <TotemInput
                            label="Choisir une commande"
                            onChange={setSearchInputValue}
                            placeholder="Rechercher par ID, numéro, organisation ou totem"
                            value={searchInputValue}
                        />
                    </SearchContainer>
                </Filters>
                <OptionContainer>
                    <TotemDatePicker
                        label="Date de livraison"
                        selected={deliverySearchDate}
                        onChange={(date) => {
                            setDeliverySearchDate(date as Date);
                        }}
                        isClearable
                    />
                    <TotemLabel>Trier par date livraison</TotemLabel>
                    <Switch
                        onChange={() => setSortByName(!sortByName)}
                        checked={sortByName}
                        onColor={colors.green}
                        offColor={colors.pantinGrey}
                        checkedIcon={false}
                        uncheckedIcon={false}
                    />
                    <TotemLabel>Trier par nom</TotemLabel>
                </OptionContainer>
            </Header>
            <Content>
                <TrelloViewContainer>
                    <TrelloView
                        initialData={initialData}
                        selectedItemIds={selectedOrderIds}
                        setSelectedItemIds={setSelectedOrderIds}
                        columnWidth="250px"
                    />
                </TrelloViewContainer>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Filters = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 10px;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const SearchContainer = styled.div`
    width: 400px;
`;

const OptionContainer = styled.div`
    display: flex;
    align-items: center;

    & > :not(:first-child) {
        margin-left: 5px;
    }
`;

const TrelloViewContainer = styled.div`
    overflow-x: scroll;
    flex: 1;
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    overflow: hidden;
`;
